import React, {
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
  useRef
} from "react";
import { statefulAnalysisTree } from "../services/AnalysisTreeServices";
import {
  NodeInsights,
} from "../containers/Interfaces";
import { useAnalysisDrawerContext } from "./AnalysisDrawerContext";
import { StatefulRequestBuilder } from "../containers/stateful/StatefulRequests";
import { MetricEdgeProto, MetricProto } from "build-ts/metadata";
import { 
  AnalysisTreeNodeProto, 
  AnalysisTreeProto, 
  CommentaryProto, 
  CommentaryProto_CommentaryConfig, 
  CommentaryProto_CommentaryConfig_SelectedNode, 
  TreeNodeMetricProto 
} from "build-ts/analysis";
import { AnalysisTreeRequestType, StatefulResponse } from "build-ts/stateful_api";
import { useChat } from "./ChatBotContext";
import { ObservationEditProto } from "build-ts/analysis_plan";
import { getMetricById } from "services/MetricService";

interface ContextType {
  getNodeData: (nodeId: string) => Promise<void>;
  nodeDataLoader: boolean;
  selectedNode: NodeInsights | null;
  setSelectedNode: React.Dispatch<React.SetStateAction<NodeInsights | null>>;
  nodeInfoMap: Map<string, NodeInsights> | null;
  setNodeInfoMap: React.Dispatch<React.SetStateAction<Map<string, NodeInsights> | null>>;
  commentaryNodes: Set<string>;
  setCommentaryNodes: React.Dispatch<React.SetStateAction<Set<string>>>;
  selectedCommentary: CommentaryProto | null;
  setSelectedCommentary: React.Dispatch<React.SetStateAction<CommentaryProto | null>>;
  generateCommentaryConfigJSON: () => CommentaryProto_CommentaryConfig | null;
  currentSelectedNode: React.MutableRefObject<string>;
  lastPosition: React.MutableRefObject<{ x: number; y: number } | null>;
  transactionId: string;
  mkgByNodeId: Map<string, TreeNodeMetricProto>; //  key is node_id
  tree: AnalysisTreeProto;
  rootNodeId: string;
  treePannelLoader: boolean;
  onEdgeClick: (node: NodeInsights,
    edge: MetricEdgeProto,
    rowIndex: number,
    transactionId: string) => void;
  onEdgeClickMKG: (node: NodeInsights,
    rowIndex: number,
    transactionId: string,
    observationEdits: ObservationEditProto[]) => void;
  getChildIdToSelect: (id: string) => string;
  setSelectedNodesToShowTables: React.Dispatch<React.SetStateAction<{node: NodeInsights, loading: boolean}[]>>;
  selectedNodesToShowTables: {node: NodeInsights, loading: boolean}[];
  isCommentaryVisible: boolean;
  setIsCommentaryVisible: React.Dispatch<React.SetStateAction<boolean>>;
  metrics: Map<string, MetricProto>;
}

const ApiContext = createContext<ContextType | undefined>(undefined);

export const useAnalysisTreeContext = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApiContext must be used within an ApiProvider");
  }
  return context;
};

export const AnalysisTreeContextProvider: React.FC<{
  children: ReactNode; 
  transactionId: string;
  tree: AnalysisTreeProto;
  nodeMetricArray: TreeNodeMetricProto[];
}> = ({ children, transactionId, tree, nodeMetricArray }) => {

  const { handleEdgeClick, handleEdgeClickMKG} = useAnalysisDrawerContext();
  const {  referencedNodeId } = useChat();
  const [selectedNode, setSelectedNode] = useState<NodeInsights | null>(null);
  const [selectedCommentary, setSelectedCommentary] = useState<CommentaryProto | null>(null);
  const [nodeDataLoader, setNodeDataLoader] = useState<boolean>(false);
  const [nodeInfoMap, setNodeInfoMap] = useState<Map<string, NodeInsights> | null>(null);
  const [commentaryNodes, setCommentaryNodes] = useState<Set<string>>(new Set());
  const [mkgByNodeId, setMkgByNodeId] = useState<Map<string, TreeNodeMetricProto>>(new Map());
  const [treePannelLoader, setTreePannelLoader] = useState<boolean>(false);
  const currentSelectedNode = useRef<string>("");
  const lastPosition = useRef<{ x: number; y: number } | null>(null);
  const [selectedNodesToShowTables, setSelectedNodesToShowTables] = useState<{node: NodeInsights, loading: boolean}[]>([]);
  const [isCommentaryVisible, setIsCommentaryVisible] = useState(true);
  const [metrics, setMetrics] = useState<Map<string, MetricProto>>(new Map());

  //Effect to get node data-table when a node is clicked from the chat
  useEffect(() => {
    if(referencedNodeId?.nodeId && referencedNodeId?.treeId === tree.header?.id){
      getNodeData(referencedNodeId?.nodeId!);
    }
  }, [referencedNodeId]);

  useEffect(() => {
    if(nodeMetricArray){
      const newMkgMap = new Map<string, TreeNodeMetricProto>(mkgByNodeId);
      // Populate the new Map with nodes from the tree
      nodeMetricArray.forEach((node: TreeNodeMetricProto) => {
        newMkgMap.set(node.node_id!, node);
      });
      setMkgByNodeId(new Map(newMkgMap));
    }

    if (tree) {
      // Create a new Map instance to ensure React detects the change  
      const newMap = new Map<string, NodeInsights>(nodeInfoMap);
        
        // Create a new Set for commentary nodes
        const newCommentaryNodes = new Set<string>();
        
        // Add each node to the map and commentary nodes set
        tree?.nodes.forEach((node: AnalysisTreeNodeProto) => {
          newMap.set(node?.id!, { current_node: node });
          // Add each node to the commentary nodes set
          newCommentaryNodes.add(node?.id!);
        });
        
        // Set the new Map to the state
        setNodeInfoMap(new Map(newMap));
        
        // Update commentary nodes to include all nodes
        setCommentaryNodes(newCommentaryNodes);
    }
  }, [tree]);

  // Function to get node data
  const getNodeData = async (nodeId: string) => {

    const node = nodeInfoMap?.get(nodeId);

    // fetch all metrics and call api if edges are not present
    for(const metric of node?.current_node?.kg_observation?.metrics || []) {
      if (metrics.has(metric.header?.id!) && metrics?.get(metric?.header?.id!)?.edges){ 
        metrics.set(metric.header?.id!, metrics.get(metric.header?.id!)!);
      }
      else{
        const metricData = await getMetricById(metric.header?.id!, true);
        if(metricData){
          metrics.set(metric.header?.id!, metricData);
        }
      }
    }
    setMetrics(metrics);
    
    if (node?.current_node.analysed_data! !== undefined) {
      setSelectedNode(node!);
    }
    else if(node?.current_node.kg_observation_data! !== undefined){
      setSelectedNode(node!);
    }
    else {
      setNodeDataLoader(true);

      const requestBuilder = new StatefulRequestBuilder(transactionId);
      const nodeRequest = requestBuilder.GetNodeRequest(nodeId);

      // Fetch the node data
      const responseData: StatefulResponse = await statefulAnalysisTree(nodeRequest);
      if (responseData) {
        const nodeData: AnalysisTreeNodeProto = responseData.analysis_tree_responses
          ?.find((resp) => resp.type === AnalysisTreeRequestType.GET_ANALYSIS_NODE)
          ?.get_analysis_node?.analysis_node!;
        nodeInfoMap?.set(nodeId, { current_node: nodeData });     
        setSelectedNode({ current_node: nodeData });
      }
      setNodeDataLoader(false);
    }
  };

  const getChildIdToSelect = (id: string): string => {
    const childNodeIds = nodeInfoMap?.get(id)?.current_node.children_ids || [];
    // Select the last child node, which was generated after the subtree was created
    const childNode = nodeInfoMap?.get(childNodeIds[childNodeIds.length - 1]);
    setSelectedNode(childNode!);
    return childNodeIds[childNodeIds.length - 1];
  };
  
  // Function to generate commentary configuration JSON
  const generateCommentaryConfigJSON = (): CommentaryProto_CommentaryConfig | null => {
    if (!nodeInfoMap) return null;

    let selectedNodes: CommentaryProto_CommentaryConfig_SelectedNode[] = [];

    if (commentaryNodes.size > 0) {
      selectedNodes = Array.from(commentaryNodes).flatMap((nodeId) => {
          const nodeMetric = nodeInfoMap.get(nodeId);
          if (nodeMetric?.current_node.result_strings) {
            let resultStrings = nodeMetric?.current_node.result_strings.join("\n");
            if(nodeMetric.insights){
             resultStrings= `${resultStrings} ${nodeMetric.insights}`.trim();
            }
            return {
              tree_node_id: nodeId,
              node_prompt: resultStrings,
            };
          }
          return [];
        });
    } else {
      selectedNodes = Array.from(nodeInfoMap.entries()).flatMap(([nodeId, nodeMetric]) => {
        if (nodeMetric.current_node.result_strings) {
          let resultStrings = nodeMetric?.current_node.result_strings.join("\n");
          if (nodeMetric.insights) {
            resultStrings = `${resultStrings} ${nodeMetric.insights}`.trim();
          }
          return {
            tree_node_id: nodeId,
            node_prompt: resultStrings,
          };
        }
        return [];
      });
    }

    if (selectedNodes.length === 0) {
      return null;
    }

    return { selected_nodes: selectedNodes };
  };

  // Only show loader for handleEdgeClick operations for each transactionId
  const onEdgeClick = async (node: NodeInsights, edge: MetricEdgeProto, rowIndex: number, transactionId: string) => {
    setTreePannelLoader(true);
    await handleEdgeClick(node?.current_node?.id!, edge, rowIndex, transactionId);
    setTreePannelLoader(false); // Reset loader after the operation
    setSelectedNode(node); // select the node for which sub-tree request was made.
  };

  const onEdgeClickMKG = async (node: NodeInsights, rowIndex: number, transactionId: string, observationEdits: ObservationEditProto[]) => {
    setTreePannelLoader(true);
    await handleEdgeClickMKG(node?.current_node?.id!, rowIndex, transactionId, observationEdits);
    setTreePannelLoader(false); // Reset loader after the operation
  };

  // Effect to handle selected commentary
  useEffect(() => {
    if (selectedCommentary) {
      let newNodeIds = new Set<string>();
      if (selectedCommentary.configuration?.selected_nodes) {
        selectedCommentary.configuration?.selected_nodes.forEach(
          (node) => {
            newNodeIds.add(node?.tree_node_id!);
          }
        );
      }
      setCommentaryNodes(newNodeIds);
    }
  }, [selectedCommentary]);
  
  return (
    <ApiContext.Provider
      value={{
        getNodeData,
        nodeDataLoader,
        selectedNode,
        setCommentaryNodes,
        commentaryNodes,
        setSelectedNode,
        nodeInfoMap,
        setNodeInfoMap,
        selectedCommentary,
        setSelectedCommentary,
        generateCommentaryConfigJSON,
        currentSelectedNode,
        transactionId,
        mkgByNodeId,
        tree,
        rootNodeId: tree.root_node_id!,
        treePannelLoader,
        onEdgeClick,
        onEdgeClickMKG,
        lastPosition,
        getChildIdToSelect,
        setSelectedNodesToShowTables,
        selectedNodesToShowTables,
        isCommentaryVisible,
        setIsCommentaryVisible,
        metrics
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};